/**
 * Custom hook for message operations using HTTP + Polling
 */

import { useCallback, useState, useEffect } from 'react';
import { unifiedMessageService, type MessageContext } from '../services/unifiedMessageService';
import { useMessageStore } from '../stores/messageStore';

// Re-export MessageContext for convenience
export type { MessageContext };

interface ImageData {
  data: string; // base64 encoded image data
  mimeType: string;
  filename?: string;
}

interface UseMessageReturn {
  sendMessage: (data: {
    conversationId: string;
    content: string;
    attachments?: string[];
    images?: ImageData[];
    model?: string;
    provider?: 'gemini' | 'claude';
    placeholderMessageId?: string;
    context?: MessageContext;  // UI context for deterministic routing
  }) => Promise<string>;
  isStreaming: boolean;
  streamingMessageId: string | null;
  error: string | null;
  clearError: () => void;
  closeStream: (conversationId: string) => void;
  hasActiveStream: (conversationId: string) => boolean;
  interruptMessage: (conversationId: string) => Promise<void>;
}

export const useMessage = (): UseMessageReturn => {
  const [error, setError] = useState<string | null>(null);
  
  const messageStore = useMessageStore();
  const isStreaming = messageStore.streamingMessageId !== null;
  const streamingMessageId = messageStore.streamingMessageId;

  // Send message using HTTP + Polling
  const sendMessage = useCallback(async (data: {
    conversationId: string;
    content: string;
    attachments?: string[];
    images?: ImageData[];
    model?: string;
    provider?: 'gemini' | 'claude';
    placeholderMessageId?: string;
    context?: MessageContext;  // UI context for deterministic routing
  }): Promise<string> => {
    try {
      setError(null);
      const messageId = await unifiedMessageService.sendMessage(data);
      return messageId;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send message';
      setError(errorMessage);
      throw error;
    }
  }, []);

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
    useMessageStore.getState().setError(null);
  }, []);

  // Close stream for a conversation
  const closeStream = useCallback((conversationId: string) => {
    unifiedMessageService.closeStream(conversationId);
  }, []);

  // Check if conversation has active stream
  const hasActiveStream = useCallback((conversationId: string) => {
    return unifiedMessageService.hasActiveStream(conversationId);
  }, []);

  // Interrupt message stream
  const interruptMessage = useCallback(async (conversationId: string) => {
    try {
      setError(null);
      await unifiedMessageService.interruptMessage(conversationId);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to interrupt message';
      setError(errorMessage);
      throw error;
    }
  }, []);

  // Debug: Log when useMessage hook is created/destroyed
  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('🎣 useMessage hook mounted');
    }
    return () => {
      if (import.meta.env.DEV) {
        console.log('🎣 useMessage hook unmounting - streams preserved');
      }
    };
  }, []);

  // Make unified message service available globally for debugging
  useEffect(() => {
    if (import.meta.env.DEV) {
      (window as any).unifiedMessageService = unifiedMessageService;
    }
  }, []);

  return {
    sendMessage,
    isStreaming,
    streamingMessageId,
    error: error || messageStore.error,
    clearError,
    closeStream,
    hasActiveStream,
    interruptMessage,
  };
};